/**
 * Ajustes do CRM de 27/10 (C1, C14): os períodos dos filtros de Empresas e Contatos como no RD ("Hoje", "Últimos 7 dias",
 * ... e "Período personalizado" com as duas datas). O preset fica no estado da tela (`<campo>_preset`) e vira as datas
 * `<campo>_desde` e `<campo>_ate` (AAAA-MM-DD, dia local) só na hora de pedir à API. Funções puras, testadas em
 * `apps/web/tests/crm2-cadastros.test.ts`.
 */
export const PRESETS_DATA = ['hoje', '7', '14', '30', '90', '180', 'personalizado'] as const;
export type PresetData = (typeof PRESETS_DATA)[number];

/** Os campos de data com período: o prefixo das chaves da API (`criado_desde`, `ultimo_contato_ate`...). */
export const CAMPOS_DATA = ['criado', 'ultimo_contato', 'ultima_venda'] as const;
export type CampoData = (typeof CAMPOS_DATA)[number];

const dia = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** As datas do preset (inclusive nas duas pontas). Personalizado e vazio não definem datas. */
export function intervaloDoPreset(p: string, agora = new Date()): { desde?: string; ate?: string } {
  if (!p || p === 'personalizado' || !(PRESETS_DATA as readonly string[]).includes(p)) return {};
  const hoje = dia(agora);
  if (p === 'hoje') return { desde: hoje, ate: hoje };
  const inicio = new Date(agora);
  inicio.setDate(inicio.getDate() - Number(p));
  return { desde: dia(inicio), ate: hoje };
}

/** Chaves só da tela (presets): ficam fora dos parâmetros da API. */
export const chaveDaTela = (k: string) => k.endsWith('_preset');

/**
 * Os filtros da tela como parâmetros da API: cada preset vira as datas (o personalizado mantém as datas digitadas; os outros
 * substituem as digitadas). Vazio sai.
 */
export function paramsDosFiltros(f: Record<string, string>, agora = new Date()): Record<string, string> {
  const saida: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v !== '' && v !== undefined && !chaveDaTela(k)) saida[k] = v;
  for (const c of CAMPOS_DATA) {
    const p = f[`${c}_preset`];
    if (!p || p === 'personalizado') continue;
    const { desde, ate } = intervaloDoPreset(p, agora);
    if (desde) saida[`${c}_desde`] = desde;
    if (ate) saida[`${c}_ate`] = ate;
  }
  return saida;
}

/** Quantos filtros do painel estão em uso (o preset e as datas do mesmo campo contam uma vez). */
export function contarFiltros(f: Record<string, string>, chaves: readonly string[]): number {
  const usados = new Set<string>();
  for (const k of chaves) {
    if (!f[k]) continue;
    const campo = CAMPOS_DATA.find((c) => k === `${c}_preset` || k === `${c}_desde` || k === `${c}_ate`);
    usados.add(campo ?? k.replace(/_(min|max)$/, ''));
  }
  return usados.size;
}
